import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useDataVaultStore, type ProductView } from '@/store/dataVault';
import { FilterRibbon } from './components/FilterRibbon';
import { ChartShell } from './components/ChartShell';
import { ChartInteractionProvider } from './components/ChartInteractionContext';
import { CrossFilterProvider } from './components/CrossFilterContext';
import { ExportMenu } from './components/ExportMenu';
import { SavedFilters } from './components/SavedFilters';
import { ComparePanel } from './components/ComparePanel';
import { AuditHistoryPanel } from './components/AuditHistoryPanel';
import { WellConstructionPanel } from './components/WellConstructionPanel';
import { AnalyticalTab } from './tabs/AnalyticalTab';
import { GaugingTab } from './tabs/GaugingTab';
import { RiskPredictionsTab } from './tabs/RiskPredictionsTab';
import { PhreaticPredictionsPanel } from './components/PhreaticPredictionsPanel';
import { PolygonPredictionsPanel } from './components/PolygonPredictionsPanel';
import { CostRemediationTab } from './tabs/CostRemediationTab';
import { PlumeSpatialTab } from './tabs/PlumeSpatialTab';
import { ComplianceTab } from './tabs/ComplianceTab';
import { QaqcTab } from './tabs/QaqcTab';
import { TerraObservationCards } from '../../components/terra/TerraObservationCards';
import { getChartType } from './components/chartTheme';
import { applyPolygonPredictions } from './utils/forecastProjection';
import { phreaticLevelsToTimeseries } from './utils/phreaticSankeyAdapter';

// 18 favorited chart components (the broader library was deleted —
// see chartTheme.ts CHART_TYPES for the canonical id → component map).
import { ConcentrationBarChart } from './components/ConcentrationBarChart';
import { ExceedanceHeatmap } from './components/ExceedanceHeatmap';
import { ConcentrationScatterChart } from './components/ConcentrationScatterChart';
import { ConcentrationDistributionChart } from './components/ConcentrationDistributionChart';
import { TrendRegressionChart } from './components/TrendRegressionChart';
import { DualAxisChart } from './components/DualAxisChart';
import { FanChart } from './components/charts/timeseries/FanChart';
import { MovingAverageChart } from './components/charts/timeseries/MovingAverageChart';
import { SparklineGrid } from './components/charts/timeseries/SparklineGrid';
import { HistogramChart } from './components/charts/distribution/HistogramChart';
import { QQPlot } from './components/charts/distribution/QQPlot';
import { StackedAreaChart } from './components/charts/composition/StackedAreaChart';
import { ConcentrationHeatmapMap } from './components/charts/spatial/ConcentrationHeatmapMap';
import { CrossSectionChart } from './components/charts/spatial/CrossSectionChart';
import { PivotHeatmap } from './components/charts/matrix/PivotHeatmap';
import { SankeyDiagram } from './components/charts/relationship/SankeyDiagram';
import { SmallMultiplesGrid } from './components/charts/interactive/SmallMultiplesGrid';
import { DataExplorer } from './components/charts/interactive/DataExplorer';

// Sprint 7: Chatbot API
import { useChartAPI } from './hooks/useChartAPI';
import { useDataVaultShortcuts } from './hooks/useDataVaultShortcuts';
import { useAvailableRegulatoryLibraries } from './hooks/useRegulatoryLines';

import {
  useFilterSites, useFilterWells, useFilterAnalytes,
  useAnalyticalResults, useAnalyticalTimeseries,
  useGaugingResults, useGaugingTimeseries, usePhreaticGaugingTimeseries,
  useRiskPredictions, usePhreaticPredictions, usePhreaticWellLevels, usePolygonWellPredictions, useCostRemediation,
  usePlumeSpatial, usePlumeGeoJSON, useCompliance,
  useAnomalies,
  useSavedPresets,
} from '@/hooks/useDataVault';
import {
  getAnalyticalCsvUrl, getGaugingCsvUrl, getXlsxExportUrl, getGeoJSONExportUrl,
  getBulkZipUrl,
  type DataExplorerFilters, type ConcentrationUnit, type ExportViewState,
} from '@/api/dataVault';
import { downloadChartAsPng, copyChartToClipboard } from './components/ChartExportUtils';
import { getQAQCDashboard } from '../../api/intake';
import type { QAQCDashboardStats } from '../../api/intake';

// Product toggle options. Only those the client has purchased are shown.
const PRODUCT_VIEWS: { key: ProductView; label: string }[] = [
  { key: 'polygon', label: 'Polygon' },
  { key: 'phreatic', label: 'Phreatic' },
];

export function DataVault() {
  const [searchParams] = useSearchParams();
  const activeChart = searchParams.get('chart');
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Determine active view from URL path
  const isChartsView = location.pathname === '/data-vault/charts';

  // Map chart data source to our data source toggle
  const chartDef = activeChart ? getChartType(activeChart) : null;

  const { products } = useAuthStore();

  // ─── Zustand store ────────────────────────────────────────────────
  const {
    activeProduct, setActiveProduct,
    horizonYears, setHorizonYears,
    dataSource, setDataSource,
    selectedSites, selectedWells, selectedAnalytes,
    dateFrom, dateTo, ndHandling, exceedsMclOnly,
    changeSites, changeWells, changeAnalytes,
    changeDateFrom, changeDateTo, changeNdHandling, changeExceedsMclOnly,
    concUnit, setConcUnit,
    showAnomalies, toggleAnomalies,
    showCompare, toggleCompare,
    showAuditHistory, toggleAuditHistory,
    selectedWellId, setSelectedWellId,
    qaqcExpanded, setQaqcExpanded,
    analyticalSort, setAnalyticalSort, analyticalPage, setAnalyticalPage,
    gaugingSort, setGaugingSort, gaugingPage, setGaugingPage,
    riskPage, setRiskPage, costPage, setCostPage,
    plumePage, setPlumePage, compliancePage, setCompliancePage,
    loadPreset,
    crossFilter, clearCrossFilter,
  } = useDataVaultStore();

  // Keyboard shortcuts
  useDataVaultShortcuts();

  // Derive activeTab from dataSource for backward compatibility
  const activeTab = useMemo(() => {
    if (dataSource === 'water-levels') return 'gauging';
    if (dataSource === 'compliance') return 'compliance';
    if (dataSource === 'qaqc') return 'qaqc';
    return 'analytical';
  }, [dataSource]);

  // QA/QC tab state (local — not persisted in presets). selectedQcTypes filters
  // by rider type (sample_type); empty = all QC types.
  const [qaqcPage, setQaqcPage] = useState(1);
  const [selectedQcTypes, setSelectedQcTypes] = useState<string[]>([]);

  const chartContainerRef = useRef<HTMLDivElement>(null);

  // QA/QC health dashboard
  const [qaqcStats, setQaqcStats] = useState<QAQCDashboardStats | null>(null);

  useEffect(() => {
    getQAQCDashboard().then(setQaqcStats).catch(() => {});
  }, []);

  // Filter data queries
  const { data: sites = [], isLoading: sitesLoading } = useFilterSites(activeProduct);
  const { data: wells = [] } = useFilterWells(selectedSites.length > 0 ? selectedSites : undefined);
  const { data: analytes = [] } = useFilterAnalytes(selectedSites.length > 0 ? selectedSites : undefined);

  // Shared filter base
  const baseFilters: DataExplorerFilters = {
    site_ids: selectedSites.length > 0 ? selectedSites : undefined,
    well_ids: selectedWells.length > 0 ? selectedWells : undefined,
    analyte_codes: selectedAnalytes.length > 0 ? selectedAnalytes : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    exceeds_mcl_only: exceedsMclOnly || undefined,
    nd_handling: ndHandling,
  };

  // Tab-specific filters
  const analyticalFilters: DataExplorerFilters = {
    ...baseFilters,
    sort_by: analyticalSort.by,
    sort_dir: analyticalSort.dir,
    page: analyticalPage,
    page_size: 50,
  };

  const gaugingFilters: DataExplorerFilters = {
    ...baseFilters,
    sort_by: gaugingSort.by,
    sort_dir: gaugingSort.dir,
    page: gaugingPage,
    page_size: 50,
  };

  const riskFilters: DataExplorerFilters = { site_ids: baseFilters.site_ids, page: riskPage, page_size: 50 };
  const costFilters: DataExplorerFilters = { site_ids: baseFilters.site_ids, page: costPage, page_size: 50 };
  const plumeFilters: DataExplorerFilters = { site_ids: baseFilters.site_ids, page: plumePage, page_size: 50 };
  const complianceFilters: DataExplorerFilters = { site_ids: baseFilters.site_ids, page: compliancePage, page_size: 50 };

  // QA/QC tab — QC riders only (sample_type != 'N'), complement of chemistry.
  const qaqcFilters: DataExplorerFilters = {
    ...baseFilters,
    qc_only: true,
    sample_types: selectedQcTypes.length > 0 ? selectedQcTypes : undefined,
    sort_by: 'sample_date',
    sort_dir: 'desc',
    page: qaqcPage,
    page_size: 50,
  };

  // Data queries — always fetch analytical and gauging timeseries (charts may need both)
  const { data: analyticalData, isLoading: analyticalLoading } = useAnalyticalResults(analyticalFilters);
  const { data: analyticalTimeseriesData } = useAnalyticalTimeseries(
    selectedSites.length > 0 ? baseFilters : { site_ids: ['__none__'] }
  );
  // Polygon per-well predicted concentrations (source of truth) — overlaid on
  // the observed series so charts/Sankey reflect the product's forecast.
  const { data: polygonPreds } = usePolygonWellPredictions(
    { site_ids: baseFilters.site_ids, page: 1, page_size: 1 },
    activeProduct === 'polygon',
    horizonYears
  );
  // Global forecast horizon: extend the analytical series to the product's
  // stored predictions at the slider year (polygon). Phreatic charts use the
  // water-level path, so this passes through unchanged for them.
  const forecastedAnalytical = useMemo(
    () =>
      analyticalTimeseriesData && horizonYears > 0 && activeProduct === 'polygon' && polygonPreds
        ? applyPolygonPredictions(analyticalTimeseriesData, polygonPreds.items, horizonYears)
        : analyticalTimeseriesData,
    [analyticalTimeseriesData, horizonYears, activeProduct, polygonPreds],
  );
  // Global scan of all libraries in the fetched dataset — used as a fallback
  // for charts that don't render regulatory lines at all (e.g., gauging-only
  // views or composition charts), so the STANDARDS bar isn't entirely empty.
  const globalLibraries = useAvailableRegulatoryLibraries(
    analyticalTimeseriesData ?? null
  );
  // Preferred source: the libraries actually rendered on the currently-active
  // chart, pushed by useRegulatoryLines as charts mount. This guarantees every
  // chip corresponds to a line that's drawn, so toggling is never a no-op.
  const currentChartLibs = useDataVaultStore((s) => s.currentChartRegulatoryLibraries);
  const availableRegulatoryLibraries =
    currentChartLibs.length > 0 ? currentChartLibs : globalLibraries;
  const { data: gaugingData, isLoading: gaugingLoading } = useGaugingResults(
    dataSource === 'water-levels' ? gaugingFilters : { page: 1, page_size: 1 }
  );
  const { data: stdGaugingData } = useGaugingTimeseries(
    selectedSites.length > 0 ? baseFilters : { site_ids: ['__none__'] }
  );
  // Phreatic water levels come from twdb + stored predictions, not the
  // monitoring-well gauging table — so route the water-level charts to the
  // phreatic feed (observed + predicted) when Phreatic is active.
  const { data: phreaticGaugingData } = usePhreaticGaugingTimeseries(
    { site_ids: baseFilters.site_ids, page: 1, page_size: 1 },
    activeProduct === 'phreatic' && selectedSites.length > 0,
    horizonYears
  );
  const timeseriesData = activeProduct === 'phreatic' ? phreaticGaugingData : stdGaugingData;
  // Risk/cost/plume queries — fetch when a chart from that category is selected
  const needsRisk = chartDef?.dataSource === 'risk';
  const needsCost = chartDef?.dataSource === 'cost';
  const needsPlume = chartDef?.dataSource === 'plume';

  const { data: riskData, isLoading: riskLoading } = useRiskPredictions(
    (needsRisk || activeProduct === 'polygon') ? riskFilters : { site_ids: ['__none__'], page: 1, page_size: 1 }
  );
  const { data: costData, isLoading: costLoading } = useCostRemediation(
    needsCost ? costFilters : { site_ids: ['__none__'], page: 1, page_size: 1 }
  );
  const { data: plumeData, isLoading: plumeLoading } = usePlumeSpatial(
    needsPlume ? plumeFilters : { site_ids: ['__none__'], page: 1, page_size: 1 }
  );
  const { data: geojsonData } = usePlumeGeoJSON(
    needsPlume ? { site_ids: baseFilters.site_ids } : { site_ids: ['__none__'] }
  );
  // Phreatic predictions — live V6 inference, only when Phreatic is the active product.
  const { data: phreaticData, isLoading: phreaticLoading } = usePhreaticPredictions(
    { site_ids: baseFilters.site_ids, page: 1, page_size: 50 },
    activeProduct === 'phreatic'
  );
  // Phreatic per-well water metrics → adapted into the Sankey's shape.
  const { data: phreaticWellLevels } = usePhreaticWellLevels(
    { site_ids: baseFilters.site_ids, page: 1, page_size: 1 },
    activeProduct === 'phreatic',
    horizonYears
  );
  const phreaticSankeyData = useMemo(
    () => (phreaticWellLevels ? phreaticLevelsToTimeseries(phreaticWellLevels.items) : null),
    [phreaticWellLevels]
  );
  const { data: complianceData, isLoading: complianceLoading } = useCompliance(
    dataSource === 'compliance' ? complianceFilters : { site_ids: ['__none__'], page: 1, page_size: 1 }
  );
  const { data: qaqcData, isLoading: qaqcDataLoading } = useAnalyticalResults(
    dataSource === 'qaqc' ? qaqcFilters : { site_ids: ['__none__'], page: 1, page_size: 1 }
  );

  // Anomaly detection
  const { data: anomalyData } = useAnomalies(baseFilters, 2.0, showAnomalies);

  // ─── Handlers ──────────────────────────────────────────────────────

  const handleLoadPreset = useCallback((filters: DataExplorerFilters, viewStatePreset?: ExportViewState) => {
    loadPreset({
      sites: filters.site_ids || [],
      wells: filters.well_ids || [],
      analytes: filters.analyte_codes || [],
      dateFrom: filters.date_from || '',
      dateTo: filters.date_to || '',
      ndHandling: filters.nd_handling || 'half_mdl',
      exceedsMclOnly: filters.exceeds_mcl_only || false,
      concUnit: viewStatePreset?.concUnit,
    });
    if (viewStatePreset?.viewMode === 'chart') {
      navigate('/data-vault/charts');
    }
  }, [loadPreset, navigate]);

  // Export URLs
  const allTabs = ['analytical', 'gauging', 'risk', 'cost', 'plume', 'compliance'];
  const csvUrlMap: Record<string, string> = {
    analytical: getAnalyticalCsvUrl(analyticalFilters),
    gauging: getGaugingCsvUrl(gaugingFilters),
  };
  const csvUrl = csvUrlMap[activeTab] || csvUrlMap.analytical;
  const xlsxUrl = getXlsxExportUrl(baseFilters, allTabs);
  const geojsonUrl = getGeoJSONExportUrl(baseFilters);
  const bulkZipUrl = getBulkZipUrl(baseFilters, allTabs);

  const viewState: ExportViewState = {
    activeTab,
    viewMode: isChartsView ? 'chart' : 'table',
    chartType: activeChart || 'timeseries',
    sortBy: activeTab === 'analytical' ? analyticalSort.by : gaugingSort.by,
    sortDir: activeTab === 'analytical' ? analyticalSort.dir : gaugingSort.dir,
    concUnit,
  };

  const { data: presets = [] } = useSavedPresets();

  const handleExportChart = useCallback(() => {
    const container = chartContainerRef.current;
    if (!container) return;
    downloadChartAsPng(container, `${activeChart || 'chart'}-${new Date().toISOString().slice(0, 10)}`);
  }, [activeChart]);

  const handleCopyChart = useCallback(() => {
    const container = chartContainerRef.current;
    if (!container) return;
    copyChartToClipboard(container);
  }, []);


  // ─── Chatbot API ──────────────────────────────────────────────────
  useChartAPI({
    activeChart: activeChart || '',
    setActiveChart: (chartType: string) => {
      // Use React Router navigate for proper state updates
      navigate(`/data-vault/charts?chart=${encodeURIComponent(chartType)}`, { replace: true });
    },
    filters: { sites: selectedSites, wells: selectedWells, analytes: selectedAnalytes, dateFrom, dateTo },
    setFilters: (f) => {
      const store = useDataVaultStore.getState();
      if (f.sites) store.setSelectedSites(f.sites);
      if (f.wells) store.setSelectedWells(f.wells);
      if (f.analytes) store.setSelectedAnalytes(f.analytes);
      if (f.dateFrom !== undefined) store.setDateFrom(f.dateFrom);
      if (f.dateTo !== undefined) store.setDateTo(f.dateTo);
    },
    chartData: analyticalTimeseriesData,
  });

  // ─── Chart Rendering ───────────────────────────────────────────────

  const renderChart = () => {
    // Feed charts the horizon-extended series so the slider projects every
    // time-series chart forward. Shadows the observed data within render only.
    const analyticalTimeseriesData = forecastedAnalytical;
    if (!activeChart) return null;

    // 18 favorited chart components, mapped from chartTheme.ts CHART_TYPES.
    switch (activeChart) {
      // ── Time Series & Trends ──
      case 'dual-axis':
        return timeseriesData ? (
          <DualAxisChart gaugingData={timeseriesData} analyticalData={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;
      case 'regression':
        return timeseriesData ? (
          <TrendRegressionChart data={timeseriesData} />
        ) : <ChartPlaceholder />;
      case 'fan':
        return analyticalTimeseriesData ? (
          <FanChart data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;
      case 'moving-average':
        return analyticalTimeseriesData ? (
          <MovingAverageChart data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;
      case 'sparkline-grid':
        return analyticalTimeseriesData ? (
          <SparklineGrid data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;

      // ── Distribution & Statistical ──
      case 'box-plot':
        return analyticalTimeseriesData ? (
          <ConcentrationDistributionChart data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;
      case 'histogram':
        return analyticalTimeseriesData ? (
          <HistogramChart data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;
      case 'qq-plot':
        return analyticalTimeseriesData ? (
          <QQPlot data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;

      // ── Comparison & Ranking ──
      case 'grouped-bar':
        return analyticalTimeseriesData ? (
          <ConcentrationBarChart data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;

      // ── Composition ──
      case 'stacked-area':
        return analyticalTimeseriesData ? (
          <StackedAreaChart data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;

      // ── Spatial & Maps ──
      case 'concentration-map':
        return analyticalTimeseriesData ? (
          <ConcentrationHeatmapMap data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;
      case 'cross-section':
        return analyticalTimeseriesData ? (
          <CrossSectionChart data={analyticalTimeseriesData} gaugingData={timeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;

      // ── Matrix & Grid ──
      case 'exceedance':
        return analyticalTimeseriesData ? (
          <ExceedanceHeatmap data={analyticalTimeseriesData} />
        ) : <ChartPlaceholder />;
      case 'pivot':
        return analyticalTimeseriesData ? (
          <PivotHeatmap data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;

      // ── Relationships & Flow ──
      case 'scatter':
        return analyticalTimeseriesData ? (
          <ConcentrationScatterChart data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;
      case 'sankey': {
        // Phreatic → water-level Sankey (site → well → range buckets of sat
        // thickness / DTW / GW elevation). Polygon/other → analyte Sankey.
        const isPhr = activeProduct === 'phreatic';
        const sankeyData = isPhr ? phreaticSankeyData : analyticalTimeseriesData;
        return sankeyData ? (
          <SankeyDiagram
            data={sankeyData}
            unit={concUnit}
            categoryLabel={isPhr ? 'Water Levels' : 'Analytes'}
            valueUnit={isPhr ? 'well(s)' : 'mg/L'}
            glassSections={isPhr}
          />
        ) : <ChartPlaceholder />;
      }

      // ── Interactive ──
      case 'small-multiples':
        return analyticalTimeseriesData ? (
          <SmallMultiplesGrid data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;
      case 'data-explorer':
        return analyticalTimeseriesData ? (
          <DataExplorer data={analyticalTimeseriesData} unit={concUnit} />
        ) : <ChartPlaceholder />;

      default:
        return <ChartComingSoon chartId={activeChart} />;
    }
  };

  // ─── Render ────────────────────────────────────────────────────────


  return (
    <CrossFilterProvider>
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h1
            className="text-lg font-medium tracking-widest font-orbitron"
            style={{ color: 'var(--dv-text-primary)' }}
          >
            DATA VAULT
          </h1>
          {/* Page navigation tabs */}
          <div
            className="flex items-center gap-0.5 rounded-lg p-0.5"
            style={{ background: 'var(--dv-bg-card)', border: '1px solid var(--dv-border-subtle)' }}
          >
            <button
              onClick={() => navigate('/data-vault')}
              className="px-3 py-1.5 text-[11px] tracking-wider rounded-md transition-all"
              style={{
                background: !isChartsView ? 'var(--dv-accent-cyan-muted)' : 'transparent',
                color: !isChartsView ? 'var(--dv-accent-cyan)' : 'var(--dv-text-muted)',
              }}
            >
              DATA
            </button>
            <button
              onClick={() => navigate('/data-vault/charts')}
              className="px-3 py-1.5 text-[11px] tracking-wider rounded-md transition-all"
              style={{
                background: isChartsView ? 'var(--dv-accent-cyan-muted)' : 'transparent',
                color: isChartsView ? 'var(--dv-accent-cyan)' : 'var(--dv-text-muted)',
              }}
            >
              CHARTS
            </button>
          </div>
          <SavedFilters currentFilters={baseFilters} viewState={viewState} onLoadPreset={handleLoadPreset} />
          {/* Cross-filter active indicator */}
          {(crossFilter.well || crossFilter.date || crossFilter.analyte) && (
            <button
              onClick={clearCrossFilter}
              aria-live="polite"
              aria-label={`Cross-filter active: ${[crossFilter.well, crossFilter.date, crossFilter.analyte].filter(Boolean).join(', ')}. Click to clear.`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-wider rounded-lg transition-all animate-fade-in focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:outline-none"
              style={{
                background: 'rgba(6, 182, 212, 0.1)',
                color: 'var(--dv-accent-cyan)',
                border: '1px solid rgba(6, 182, 212, 0.3)',
              }}
            >
              FILTERED: {[crossFilter.well, crossFilter.date, crossFilter.analyte].filter(Boolean).join(' / ')}
              <span className="ml-1 opacity-60">&times;</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* Unit toggle */}
          <div
            className="flex items-center gap-0.5 rounded-lg p-0.5"
            style={{ background: 'var(--dv-bg-card)', border: '1px solid var(--dv-border-subtle)' }}
          >
            {(['mg/L'] as ConcentrationUnit[]).map((u) => (
              <button
                key={u}
                onClick={() => setConcUnit(u)}
                className="px-2.5 py-1 text-[11px] tracking-wider rounded-md transition-all"
                style={{
                  background: concUnit === u ? 'var(--dv-accent-cyan-muted)' : 'transparent',
                  color: concUnit === u ? 'var(--dv-accent-cyan)' : 'var(--dv-text-muted)',
                }}
              >
                {u}
              </button>
            ))}
          </div>

          {/* Anomaly toggle */}
          <button
            onClick={toggleAnomalies}
            aria-pressed={showAnomalies}
            className="px-2.5 py-1.5 text-[10px] tracking-wider rounded-lg transition-all focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:outline-none"
            style={{
              background: showAnomalies ? 'rgba(245, 158, 11, 0.1)' : 'var(--dv-bg-card)',
              color: showAnomalies ? '#f59e0b' : 'var(--dv-text-muted)',
              border: `1px solid ${showAnomalies ? 'rgba(245, 158, 11, 0.3)' : 'var(--dv-border-subtle)'}`,
            }}
          >
            ANOMALIES
          </button>

          {/* Compare toggle */}
          <button
            onClick={toggleCompare}
            aria-pressed={showCompare}
            className="px-2.5 py-1.5 text-[10px] tracking-wider rounded-lg transition-all focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:outline-none"
            style={{
              background: showCompare ? 'rgba(59, 130, 246, 0.1)' : 'var(--dv-bg-card)',
              color: showCompare ? '#3b82f6' : 'var(--dv-text-muted)',
              border: `1px solid ${showCompare ? 'rgba(59, 130, 246, 0.3)' : 'var(--dv-border-subtle)'}`,
            }}
          >
            COMPARE
          </button>

          {/* Audit log toggle */}
          <button
            onClick={toggleAuditHistory}
            aria-pressed={showAuditHistory}
            className="px-2.5 py-1.5 text-[10px] tracking-wider rounded-lg transition-all focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:outline-none"
            style={{
              background: showAuditHistory ? 'rgba(139, 92, 246, 0.1)' : 'var(--dv-bg-card)',
              color: showAuditHistory ? '#8b5cf6' : 'var(--dv-text-muted)',
              border: `1px solid ${showAuditHistory ? 'rgba(139, 92, 246, 0.3)' : 'var(--dv-border-subtle)'}`,
            }}
          >
            AUDIT LOG
          </button>

          <ExportMenu
            viewState={viewState}
            csvUrl={csvUrl}
            xlsxUrl={xlsxUrl}
            geojsonUrl={geojsonUrl}
            bulkZipUrl={bulkZipUrl}
            presets={presets}
            onLoadPreset={(preset) => handleLoadPreset(preset.filters_json, preset.filters_json?._view)}
            onExportChart={activeChart ? handleExportChart : undefined}
            onCopyChart={activeChart ? handleCopyChart : undefined}
          />
        </div>
      </div>

      {/* TERRA proactive observations — Data Vault findings. Scoped by selected site if one is chosen. */}
      <TerraObservationCards
        product="data-vault"
        siteId={selectedSites.length === 1 ? selectedSites[0] : undefined}
        title="TERRA Noticed — Data Vault"
        limit={5}
      />

      {/* Product toggle — swaps which purchased product's data feeds the tabs.
          Tabs stay the same; only the underlying data domain changes. */}
      {(() => {
        const owned = PRODUCT_VIEWS.filter((p) => products?.includes(p.key));
        if (owned.length === 0) return null;
        return (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] uppercase tracking-wide opacity-60">Product</span>
            {owned.map((p) => (
              <button
                key={p.key}
                onClick={() => {
                  setActiveProduct(p.key);
                  // Swap the data domain to match the product:
                  // polygon → analyte concentrations (chemistry),
                  // phreatic → water availability (water levels).
                  setDataSource(p.key === 'phreatic' ? 'water-levels' : 'chemistry');
                  // Site lists differ per product (polygon `sites` vs phreatic
                  // `phreatic_sites`), so clear stale selections on switch.
                  changeSites([]);
                }}
                className={`text-xs rounded-full px-3 py-1 border transition-colors ${
                  activeProduct === p.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-400/40 opacity-70 hover:opacity-100'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        );
      })()}

      {/* ML forecast horizon — global slider that drives every chart's projection.
          Time-series charts plot the forecast forward to this year; static
          diagrams (e.g. Sankey) render the snapshot at this year. */}
      <div className="flex items-center gap-3 mb-2">
        <span className="text-[11px] uppercase tracking-wide opacity-60">Forecast horizon</span>
        <input
          type="range"
          min={0}
          max={20}
          step={1}
          value={horizonYears}
          onChange={(e) => setHorizonYears(Number(e.target.value))}
          className="w-48 accent-blue-600"
        />
        <span className="text-xs tabular-nums opacity-80 w-14">
          {horizonYears === 0 ? 'Now' : `+${horizonYears} yr`}
        </span>
      </div>

      {/* Polygon predictions — shown when Polygon is active */}
      {activeProduct === 'polygon' && (
        <PolygonPredictionsPanel data={riskData} isLoading={riskLoading} />
      )}

      {/* Phreatic predictions — on-load V6 forecast, shown when Phreatic is active */}
      {activeProduct === 'phreatic' && (
        <PhreaticPredictionsPanel data={phreaticData} isLoading={phreaticLoading} horizonYears={horizonYears} />
      )}

      {/* Filter Ribbon */}
      <FilterRibbon
        sites={sites}
        wells={wells}
        analytes={analytes}
        sitesLoading={sitesLoading}
        selectedSites={selectedSites}
        selectedWells={selectedWells}
        selectedAnalytes={selectedAnalytes}
        dateFrom={dateFrom}
        dateTo={dateTo}
        ndHandling={ndHandling}
        exceedsMclOnly={exceedsMclOnly}
        dataSource={dataSource}
        onDataSourceChange={setDataSource}
        regulatoryLibraries={availableRegulatoryLibraries}
        onSitesChange={changeSites}
        onWellsChange={changeWells}
        onAnalytesChange={changeAnalytes}
        onDateFromChange={changeDateFrom}
        onDateToChange={changeDateTo}
        onNdHandlingChange={changeNdHandling}
        onExceedsMclOnlyChange={changeExceedsMclOnly}
      />

      {/* QA/QC Health Widget */}
      {qaqcStats && qaqcStats.total_results > 0 && (
        <div
          className="rounded-xl border overflow-hidden"
          style={{ borderColor: 'var(--dv-border-subtle)' }}
        >
          <button
            onClick={() => setQaqcExpanded(!qaqcExpanded)}
            className="w-full px-4 py-2.5 flex items-center justify-between transition-colors"
            style={{ background: 'var(--dv-bg-card)' }}
          >
            <div className="flex items-center gap-3">
              <span className="text-xs font-mono tracking-wider" style={{ color: 'var(--dv-accent-cyan)' }}>QA/QC STATUS</span>
              <span className="text-xs" style={{ color: 'var(--dv-text-muted)' }}>Last 90 days</span>
            </div>
            <div className="flex items-center gap-4 text-xs font-mono">
              <span style={{ color: 'var(--dv-status-success)' }}>{qaqcStats.usable_pct}% Usable</span>
              <span style={{ color: 'var(--dv-status-warning)' }}>{qaqcStats.estimated_pct}% Estimated</span>
              <span style={{ color: 'var(--dv-status-danger)' }}>{qaqcStats.rejected_pct}% Rejected</span>
              <span style={{ color: 'var(--dv-text-muted)' }}>{qaqcExpanded ? '\u25B2' : '\u25BC'}</span>
            </div>
          </button>
          {qaqcExpanded && (
            <div className="px-4 py-3 border-t" style={{ borderColor: 'var(--dv-border-subtle)' }}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-xs tracking-wider mb-2" style={{ color: 'var(--dv-text-muted)' }}>TOP ISSUES</h4>
                  {qaqcStats.top_issues.length > 0 ? (
                    <div className="space-y-1">
                      {qaqcStats.top_issues.slice(0, 5).map((issue, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span style={{ color: 'var(--dv-text-secondary)' }}>{issue.issue.replace(/_/g, ' ')}</span>
                          <span className="font-mono" style={{ color: 'var(--dv-status-danger)' }}>{issue.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs" style={{ color: 'var(--dv-text-muted)' }}>No issues found</p>
                  )}
                </div>
                <div>
                  <h4 className="text-xs tracking-wider mb-2" style={{ color: 'var(--dv-text-muted)' }}>RECENT QA/QC RUNS</h4>
                  {qaqcStats.recent_batches.length > 0 ? (
                    <div className="space-y-1">
                      {qaqcStats.recent_batches.map((batch, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="truncate max-w-[200px]" style={{ color: 'var(--dv-text-secondary)' }}>{batch.file_name}</span>
                          <div className="flex gap-2">
                            {batch.rejected_count > 0 && <span style={{ color: 'var(--dv-status-danger)' }}>{batch.rejected_count}R</span>}
                            {batch.qualified_count > 0 && <span style={{ color: 'var(--dv-status-warning)' }}>{batch.qualified_count}Q</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs" style={{ color: 'var(--dv-text-muted)' }}>No recent runs</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Compare Panel */}
      {showCompare && (
        <ComparePanel sites={sites} selectedSites={selectedSites} onClose={toggleCompare} />
      )}

      {/* Audit History Panel */}
      {showAuditHistory && (
        <AuditHistoryPanel onClose={toggleAuditHistory} />
      )}

      {/* Well Construction Panel */}
      {selectedWellId && (
        <WellConstructionPanel
          wellId={selectedWellId}
          onClose={() => setSelectedWellId(null)}
          onElevationHistoryChanged={() => queryClient.invalidateQueries({ queryKey: ['data-vault'] })}
        />
      )}


      {/* Charts View */}
      {isChartsView && (
        <div ref={chartContainerRef} className="animate-fade-in">
          <ChartInteractionProvider>
            <ChartShell chartId={activeChart}>
              {renderChart()}
            </ChartShell>
          </ChartInteractionProvider>
        </div>
      )}

      {/* Data Grid View */}
      {!isChartsView && (
        <div className="animate-fade-in">
          {activeTab === 'analytical' && (
            <AnalyticalTab
              data={analyticalData}
              isLoading={analyticalLoading}
              page={analyticalPage}
              onPageChange={setAnalyticalPage}
              sortBy={analyticalSort.by}
              sortDir={analyticalSort.dir}
              onSortChange={setAnalyticalSort}
              unit={concUnit}
              ndHandling={ndHandling}
              anomalies={anomalyData}
              showAnomalies={showAnomalies}
              onSelectWell={setSelectedWellId}
            />
          )}

          {activeTab === 'gauging' && (
            <GaugingTab
              data={gaugingData}
              isLoading={gaugingLoading}
              page={gaugingPage}
              onPageChange={setGaugingPage}
              sortBy={gaugingSort.by}
              sortDir={gaugingSort.dir}
              onSortChange={setGaugingSort}
              onSelectWell={setSelectedWellId}
            />
          )}

          {activeTab === 'compliance' && (
            <ComplianceTab
              data={complianceData}
              isLoading={complianceLoading}
              page={compliancePage}
              onPageChange={setCompliancePage}
            />
          )}

          {activeTab === 'qaqc' && (
            <QaqcTab
              data={qaqcData}
              isLoading={qaqcDataLoading}
              page={qaqcPage}
              onPageChange={setQaqcPage}
              selectedTypes={selectedQcTypes}
              onTypesChange={(t) => { setSelectedQcTypes(t); setQaqcPage(1); }}
              onSelectWell={setSelectedWellId}
            />
          )}
        </div>
      )}
    </div>
    </CrossFilterProvider>
  );
}

// ─── Placeholder Components ──────────────────────────────────────────

function ChartPlaceholder() {
  return (
    <div className="flex items-center justify-center py-20">
      <p className="text-sm tracking-wider" style={{ color: 'var(--dv-text-muted)' }}>
        Select a site and wells to load data
      </p>
    </div>
  );
}

function ChartComingSoon({ chartId }: { chartId: string }) {
  const chartDef = getChartType(chartId);
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <div className="text-4xl opacity-20">&#128736;</div>
      <p className="text-sm font-medium tracking-wider" style={{ color: 'var(--dv-text-secondary)' }}>
        {chartDef?.name || chartId}
      </p>
      <p className="text-xs" style={{ color: 'var(--dv-text-muted)' }}>
        {chartDef?.description || 'This visualization is coming soon'}
      </p>
      <span
        className="text-[9px] px-2 py-1 rounded-full"
        style={{
          background: 'rgba(16, 185, 129, 0.1)',
          color: 'var(--dv-accent-emerald)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
        }}
      >
        COMING IN NEXT SPRINT
      </span>
    </div>
  );
}
